import React, { useState, useRef, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, useScroll, useSpring, AnimatePresence } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { NAV_LINKS, ROUTE_LINKS } from "@/content";
import { useDarkMode } from "@/lib/darkMode";
import { usePresence } from "@/lib/usePresence";
import Wordmark from "@/components/Wordmark";

export default function Nav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { dark, toggle } = useDarkMode();
  const presence = usePresence();
  const navigate = useNavigate();
  const location = useLocation();
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 60,
    restDelta: 0.001,
  });

  const closeRef = useRef(null);

  // Escape key closes the mobile drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // Move focus to close button when drawer opens
  useEffect(() => {
    if (mobileOpen) closeRef.current?.focus();
  }, [mobileOpen]);

  const handleJump = (id) => () => {
    // If we're not on the homepage, the section anchors don't exist in the
    // DOM yet — navigate to `/#id` first and let App.js's ScrollToTop pick
    // up the hash and scroll once the homepage has mounted.
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (!el) return;
    if (window.__lenis) {
      const top = el.getBoundingClientRect().top + window.scrollY - 48;
      window.__lenis.scrollTo(top, { duration: 1.2 });
    } else {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
    <header
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-white/60 dark:bg-[#0D1117]/85 border-b border-black/5 dark:border-white/7"
      data-testid="site-nav"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-14 flex items-center justify-between">
        <a
          href="/"
          onClick={(e) => {
            e.preventDefault();
            // On homepage: scroll to top. On any other route: navigate home.
            if (location.pathname === "/") {
              if (window.__lenis) { window.__lenis.scrollTo(0, { duration: 1.2 }); } else { document.documentElement.scrollTop = 0; }
            } else {
              navigate("/");
            }
          }}
          className="inline-flex items-center gap-2.5 hover:opacity-70 transition-opacity focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          data-testid="nav-brand"
          data-cursor="magnetic"
          data-cursor-label="HOME"
        >
          <Wordmark version />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={handleJump(l.id)}
              data-cursor="magnetic"
              className="font-display font-medium text-[14px] tracking-[-0.01em] text-mute hover:text-ink transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          {ROUTE_LINKS.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-cursor="magnetic"
              className="relative font-display font-medium text-[14px] tracking-[-0.01em] text-ink hover:opacity-80 transition-opacity"
              data-testid={`nav-link-${l.id}`}
            >
              <span className="relative z-10">{l.label}</span>
              <span className="absolute -bottom-1 left-0 right-0 h-[3px] bg-[#FFD93D]" aria-hidden />
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Live presence — pulses green when Sam's at the desk, amber if
              he stepped away recently, muted otherwise. See lib/usePresence
              and netlify/functions/presence*. */}
          <span
            className="hidden lg:inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-mute"
            data-testid="presence-indicator"
            title={
              presence?.lastSeenAt
                ? `Last ping: ${new Date(presence.lastSeenAt).toLocaleString("en-GB", { timeZone: "Europe/London", weekday: "short", hour: "2-digit", minute: "2-digit" })} GMT`
                : ""
            }
          >
            <span className="relative flex h-1.5 w-1.5">
              {presence?.state === "here" && (
                <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
              )}
              <span
                className={`relative rounded-full h-1.5 w-1.5 ${
                  presence?.state === "here"   ? "bg-emerald-500"
                  : presence?.state === "around" ? "bg-amber-400"
                  : "bg-mute opacity-60"
                }`}
              />
            </span>
            {presence?.label || "Open for conversation"}
          </span>

          {/* Dark mode toggle */}
          <button
            onClick={toggle}
            data-cursor="magnetic"
            data-cursor-label="THEME"
            aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-black/10 dark:border-white/10 text-mute hover:text-ink transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
          >
            {dark
              ? <Sun  size={13} strokeWidth={2} />
              : <Moon size={13} strokeWidth={2} />
            }
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden flex flex-col gap-[5px] justify-center items-center w-9 h-9 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
            aria-label="Open navigation"
            data-testid="nav-mobile-open"
          >
            <span className="block w-5 h-[1.5px] bg-ink" />
            <span className="block w-5 h-[1.5px] bg-ink" />
            <span className="block w-3 h-[1.5px] bg-ink" />
          </button>
        </div>
      </div>

      <motion.div
        className="absolute left-0 bottom-0 h-[2px] bg-ink origin-left"
        style={{ scaleX: progress, width: "100%" }}
        data-testid="scroll-progress-bar"
      />
    </header>

    {/* Mobile nav drawer */}
    <AnimatePresence>
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] bg-ink/40 backdrop-blur-sm lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <motion.div
            key="drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-[80vw] max-w-[320px] bg-paper dark:bg-[#161B22] flex flex-col lg:hidden"
            data-testid="nav-mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-6 h-14 border-b border-black/8">
              <span className="font-mono text-[11px] tracking-[0.28em] text-ink">MENU</span>
              <button
                ref={closeRef}
                onClick={() => setMobileOpen(false)}
                className="w-8 h-8 flex items-center justify-center text-mute hover:text-ink transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                aria-label="Close navigation"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <nav className="flex flex-col px-6 pt-8 gap-1 flex-1">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => { setMobileOpen(false); handleJump(l.id)(); }}
                  className="text-left py-3.5 border-b border-black/6 font-serif text-[22px] tracking-tight text-ink hover:text-mute transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                >
                  {l.label}
                </motion.button>
              ))}
              {ROUTE_LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + (NAV_LINKS.length + i) * 0.04, duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    className="block py-3.5 border-b border-black/6 font-serif text-[22px] tracking-tight text-ink hover:text-mute transition-colors"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
            </nav>

            {/* Drawer footer */}
            <div className="px-6 pb-10 pt-6">
              <div className="flex items-center gap-2 mb-5">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-60" />
                  <span className="relative rounded-full h-1.5 w-1.5 bg-emerald-500" />
                </span>
                <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-mute">Open for conversation</span>
              </div>
              <button
                onClick={() => { setMobileOpen(false); window.dispatchEvent(new CustomEvent("erpik:open-contact", { detail: { subject: "General enquiry" } })); }}
                className="block w-full py-3 text-center rounded-full bg-ink text-paper font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-ink-soft transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
              >
                Get in touch
              </button>
              <button
                onClick={toggle}
                className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-full border border-black/10 dark:border-white/10 font-mono text-[10px] tracking-[0.22em] uppercase text-mute hover:text-ink transition-colors"
              >
                {dark ? <Sun size={12} strokeWidth={2} /> : <Moon size={12} strokeWidth={2} />}
                {dark ? "Light mode" : "Dark mode"}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
    </>
  );
}
